const DEFAULT_API_URL = 'http://127.0.0.1:3001/api/v1'

declare global {
  interface Window {
    __MYADC_CONFIG__?: {
      apiUrl?: string
    }
  }
}

const runtimeApiUrl = typeof window !== 'undefined'
  ? window.__MYADC_CONFIG__?.apiUrl?.trim()
  : undefined

/**
 * SPFx supplies the production API URL at runtime through the web-part property
 * pane. Standalone Vite builds can still use VITE_API_URL.
 */
export const API_BASE_URL = (runtimeApiUrl || import.meta.env.VITE_API_URL || DEFAULT_API_URL)
  .replace(/\/+$/, '')

export type ApiFacility = {
  id: string
  code: string
  name: string
  timezone: string
  isDefault: boolean
}

export type ApiUser = {
  id: string
  organizationId: string
  email: string
  displayName: string
  facilities: ApiFacility[]
  roles: string[]
  permissions: string[]
}

export type AuthSession = {
  accessToken: string
  accessTokenExpiresIn: number
  activeFacilityId: string
  user: ApiUser
}

type ApiEnvelope<T> = {
  data: T
  meta?: Record<string, unknown>
}

type ProblemDetails = {
  status?: number
  title?: string
  detail?: string
  errors?: string[]
}

export class ApiError extends Error {
  readonly status: number
  readonly details?: ProblemDetails

  constructor(status: number, message: string, details?: ProblemDetails) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.details = details
  }
}

let accessToken: string | null = null
let activeFacilityId: string | null = null
let refreshPromise: Promise<AuthSession> | null = null

function applySession(session: AuthSession): AuthSession {
  accessToken = session.accessToken
  activeFacilityId = session.activeFacilityId
  return session
}

export function clearApiSession(): void {
  accessToken = null
  activeFacilityId = null
}

async function readError(response: Response): Promise<ApiError> {
  let details: ProblemDetails | undefined
  try {
    details = await response.json() as ProblemDetails
  } catch {
    details = undefined
  }
  return new ApiError(
    response.status,
    details?.detail || details?.title || `Request failed with status ${response.status}.`,
    details,
  )
}

async function sessionRequest(path: 'login' | 'refresh', body?: unknown): Promise<AuthSession> {
  const response = await fetch(`${API_BASE_URL}/auth/${path}`, {
    method: 'POST',
    credentials: 'include',
    headers: body ? { 'Content-Type': 'application/json' } : undefined,
    body: body ? JSON.stringify(body) : undefined,
  })
  if (!response.ok) throw await readError(response)
  const payload = await response.json() as ApiEnvelope<AuthSession>
  return applySession(payload.data)
}

export function login(email: string, password: string): Promise<AuthSession> {
  return sessionRequest('login', { email, password })
}

export function refreshSession(): Promise<AuthSession> {
  if (!refreshPromise) {
    refreshPromise = sessionRequest('refresh').finally(() => {
      refreshPromise = null
    })
  }
  return refreshPromise
}

export async function logout(): Promise<void> {
  try {
    await fetch(`${API_BASE_URL}/auth/logout`, {
      method: 'POST',
      credentials: 'include',
      headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : undefined,
    })
  } finally {
    clearApiSession()
  }
}

/** Authenticated request helper for the remaining frontend modules. */
export async function apiRequest<T>(path: string, init: RequestInit = {}, retry = true): Promise<T> {
  const headers = new Headers(init.headers)
  if (init.body && !headers.has('Content-Type')) headers.set('Content-Type', 'application/json')
  if (accessToken) headers.set('Authorization', `Bearer ${accessToken}`)
  if (activeFacilityId) headers.set('X-Facility-Id', activeFacilityId)

  const response = await fetch(`${API_BASE_URL}/${path.replace(/^\/+/, '')}`, {
    ...init,
    headers,
    credentials: 'include',
  })

  if (response.status === 401 && retry) {
    await refreshSession()
    return apiRequest<T>(path, init, false)
  }
  if (!response.ok) throw await readError(response)
  if (response.status === 204) return undefined as T
  return response.json() as Promise<T>
}
